import { http } from '@/providers/apis/http';
import {
  GroupsReportRequest,
  GroupsCompetencyReportRequest,
  GroupsPerformanceRequest} from '@/models/groups/request-param';
import { groupsSerializer } from '@/providers/serializers/groups/groups';
import { classGroupSerializer } from '@/providers/serializers/groups/class';
import { group } from 'd3';
import { classSerializer } from '@/providers/serializers/class/class';

export class GroupsAPI {
  public static INSTANCE = new GroupsAPI();

  public typeArrayItem: any = [];

  private namespace: string = 'api/reports/v1/groups';

  private adminNamespace: string = 'api/admin/v1';

  private missionControlNamespace = 'api/missioncontrol/v1';

  private navigatorReportversion: string = 'api/v2/navigator-report';

  private reportNamespace: string = 'api/ds/users/v2/class';

  private namespaceStub = `${window.location.origin}/stubs`;

  private namespaceV2: string = 'api/ds/users/v2';

  public fetchGroupCountries() {
    const endpoint = `${this.namespace}/countries`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return groupsSerializer.serializeGroupCountries(response.data);
    }).catch((error: any) => {
      return { message: 'error' };
    });
  }

  public fetchUserHierarchies() {
    const endpoint = `${this.namespace}/hierarchies/user`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return groupsSerializer.serializeUserHierarchies(response.data);
    }).catch((error: any) => {
      return { message: 'error' };
    });
  }

  public fetchHierarchyMapping(params: any) {
    const endpoint = `${this.adminNamespace}/group/hierarchy/mapping`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeMappingData(response.data);
    }).catch((error: any) => {
      return [];
    });
  }

  public fetchClassData(params: any) {
    const endpoint = `${this.missionControlNamespace}/school/classes`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeClassData(response.data);
    }).catch((error: any) => {
      return [];
    });
  }

  public fetchGroupedHierarchyMapping(params: any) {
      const endpoint = `${this.adminNamespace}/group/hierarchy/mapping/all`;
      const headers = http.getTokenHeaders();
      return http.get(endpoint, headers, params).then((response) => {
        const dataList: any = groupsSerializer.serializeGroupedHierarchyMapping(response.data);
        this.typeArrayItem[params.type] = dataList;
        return dataList;
      }).catch((error: any) => {
        return [];
      });
  }

  public fetchUserACL(param: any) {
    const endpoint = `${this.adminNamespace}/groups/user/${param.userId}/tenant/${param.tenantId}/acl`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
        return groupsSerializer.normalizeACLDemoList(response.data);
    }).catch((error: any) => {
        return { message: 'error' };
    });
  }

  public fetchGroupReport(params: GroupsReportRequest) {
    const endpoint = `${this.namespace}`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeGroupsReport(response.data);
    }).catch((error: any) => {
      return { message: 'error' };
    });
  }

  public fetchGroupsCompetencyReport(params: GroupsCompetencyReportRequest) {
    const endpoint = `${this.namespace}/competency`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeGroupsReport(response.data);
    });
  }


  public fetchClassCompetencyReport(params: any) {
    const endpoint = `${this.namespace}/competency/class`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return classGroupSerializer.serializeClassCompetency(response.data);
    });
  }

  public fetchClassTimespentReport(params: any) {
    const endpoint = `${this.namespace}/timespent/class`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return classGroupSerializer.serializeClassTimespent(response.data);
    });
  }

  public fetchGroupsPerformance(params: GroupsPerformanceRequest) {
    const endpoint = `${this.namespace}/performance`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeGroupPerformance(response.data);
    });
  }

  public fetchGroupsTimespent(params: GroupsPerformanceRequest) {
    const endpoint = `${this.namespace}/timespent`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.serializeGroupTimespent(response.data);
    });
  }

  public fetchGroupsStub(groupId?: string | number) {
    const endpoint = `${this.namespaceStub}/groups/${groupId}.json`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers).then((response) => {
      return response.data;
    });
  }

  public fetchClassProgressReport(classId: string, params: any) {
    const endpoint = `${this.reportNamespace}/${classId}/student/summary`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return classGroupSerializer.serializeClassProgressReport(response.data);
    });
  }

  public fetchGroupSubjects(params: any) {
    const endpoint = `${this.namespace}/subjects`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.groupsSubjectList(response.data);
    });
  }

  public fetchGroupUsers(params: any) {
   const endpoint = `${this.namespace}/users`;
   const headers = http.getTokenHeaders();
   return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.normalizeUsers(response.data);
    }).catch((err) => {
      return {};
    });
  }

  public fetchClassUsers(params: any) {
    const endpoint = `${this.namespace}/users/class`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.normalizeUsers(response.data);
    }).catch((err) => {
      return {};
    });
  }

  public fetchStugglingCompetency(params: any) {
    const endpoint = `${this.namespace}/competency/struggling`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.normalizeStrugglingCompetency(response.data);
    }).catch((err) => {
      return {};
    });
  }

  public fetchClassStruggleCompetency(params: any) {
    const endpoint = `${this.namespaceV2}/competencies/struggling`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return groupsSerializer.normalizeStrugglingGrades(response.data);
    });
  }

  public fetchCompetencyReportData(params: any) {
    const endpoint = `${this.namespace}/stats/competency`;
    const headers = http.getTokenHeaders();
    return http.get(endpoint, headers, params).then((response) => {
      return classSerializer.normalizeCompetencyGroupsData(response.data);
    });
  }
}

export const groupsApi = GroupsAPI.INSTANCE;
